import { isSameRect } from './placeLabel';
import type { Vector3 } from '@kitware/vtk.js/types';
import type { Rect } from './placeLabel';

/** What a placed label shares with the other labels in its viewport (CSS px, y down). */
export interface LabelEntry {
  /** Where the annotation the label describes is on screen. */
  anchor: Rect;
  /** Where the label ended up, or null until it has been measured and placed. */
  label: Rect | null;
  /** The label is fixed in place, so every other label stays clear of it whatever the id order. */
  settled?: boolean;
}

export type LabelEntries = ReadonlyMap<string, LabelEntry>;

const EMPTY: LabelEntries = new Map();

// Snapshots are replaced, never mutated, so they work with useSyncExternalStore.
const snapshots = new Map<string, LabelEntries>();
const listeners = new Set<() => void>();

function publish(viewportId: string, entries: LabelEntries): void {
  if (entries.size === 0) snapshots.delete(viewportId);
  else snapshots.set(viewportId, entries);
  listeners.forEach((listener) => listener());
}

export function setLabelEntry(viewportId: string, id: string, entry: LabelEntry): void {
  const current = snapshots.get(viewportId) ?? EMPTY;
  const existing = current.get(id);
  // Skipping no-op writes is what stops labels from re-notifying each other forever.
  if (
    existing != null &&
    isSameRect(existing.anchor, entry.anchor) &&
    isSameRect(existing.label, entry.label) &&
    existing.settled === entry.settled
  ) {
    return;
  }

  publish(viewportId, new Map(current).set(id, entry));
}

export function removeLabelEntry(viewportId: string, id: string): void {
  const current = snapshots.get(viewportId);
  if (current == null || !current.has(id)) return;

  const next = new Map(current);
  next.delete(id);
  publish(viewportId, next);
}

export function getLabelEntries(viewportId: string): LabelEntries {
  return snapshots.get(viewportId) ?? EMPTY;
}

export function subscribeToLabelEntries(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Rects a label must stay clear of: every other annotation, plus the labels placed before it and
 * any label that is fixed in place. Labels still being placed only yield to ones with a smaller
 * id, so two of them can never keep pushing each other around: the order is fixed and the result
 * settles after one pass.
 */
export function getKeepOutRects(entries: LabelEntries, id: string): Rect[] {
  const rects: Rect[] = [];

  entries.forEach((entry, otherId) => {
    if (otherId === id) return;
    rects.push(entry.anchor);
    if (entry.label != null && (entry.settled || otherId < id)) rects.push(entry.label);
  });

  return rects;
}

// Where each label was first placed, as a world offset from its annotation's label anchor to the
// label's top left corner. World space, so it follows the annotation through pan and zoom. Kept
// outside React so a label keeps its spot when it remounts, e.g. after scrolling away and back.
const labelOffsets = new Map<string, Vector3>();

const offsetKey = (viewportId: string, id: string) => `${viewportId}:${id}`;

export function getLabelOffset(viewportId: string, id: string): Vector3 | undefined {
  return labelOffsets.get(offsetKey(viewportId, id));
}

export function setLabelOffset(viewportId: string, id: string, offset: Vector3): void {
  const existing = labelOffsets.get(offsetKey(viewportId, id));
  // Skipping no-op writes is what stops labels from re-notifying each other forever.
  if (existing != null && existing.every((value, index) => value === offset[index])) return;

  labelOffsets.set(offsetKey(viewportId, id), offset);
  listeners.forEach((listener) => listener());
}

export function removeLabelOffset(viewportId: string, id: string): void {
  labelOffsets.delete(offsetKey(viewportId, id));
}
